/**
 * drum-track-detect.js — «is this a DRUM TRACK, or a whole song?», answered from
 * the INPUT, before any model runs.
 *
 * Why it exists (feedback 2026-09-18, /stem-splitter: «Я хотел удалить барабаны
 * из аудио но они не убрались»). The reporter had put a whole song into
 * /drum-separator. Its three models were trained on drum-only audio, so on a
 * song they do worse than nothing — measured on MUSDB18-test, 50 clips: the
 * «mute kick+snare+toms» file scores 2.67 dB SI-SDR against the true drumless
 * mix where the UNTOUCHED song scores 4.03; the drums come down by 7 dB and the
 * music is damaged by 3. The tool did have a whole-song warning, but it was
 * derived from the MODELS' output (restShare > 0.40, calibrated on ONE song):
 * on a labelled set it missed 21.5 % of songs (30 % of instrumentals) and
 * accused 16 % of real drum tracks. A rule over the output of models that were
 * never shown a song cannot be calibrated — their answer there is arbitrary.
 * It also arrived AFTER the 80-second split and after the day's unit was spent.
 *
 * What does separate the classes is in the input itself:
 *   hpss     — share of energy that a median-filter HPSS (Fitzgerald 2010) calls
 *              harmonic, 150–6000 Hz. Sustained pitched sound (voice, bass line,
 *              chords) is what a song has and a kit does not.      AUC 0.98
 *   floorDb  — 10th/90th percentile of frame energy. A kit falls quiet between
 *              hits; a song's bed of sustained instruments does not. AUC 0.98
 * (tonal-peak persistence 0.91 and mid-band flatness 0.79 were measured and
 * added nothing to these two.) Both are taken PER 4-SECOND CHUNK and the file's
 * verdict is the mean of its chunks' — pooled over a whole file the floor reads a
 * song's quiet intro as «a kit falling silent», and the first draft called such a
 * song drums at p = 0.002. The weights were fitted on THIS module's own output
 * (tests/js/drum-track-detect.test.mjs keeps the behaviour; the labelled set and
 * the fit live in the lab, sepbench/dfm — MUSDB18 audio cannot live in the repo).
 *
 * The verdict has three values on purpose. 'song' changes what the page offers;
 * 'drums' changes nothing; 'unsure' changes nothing either, but is reported — a
 * 7-second drum-only intro of a song, or an 808 line that sustains like a bass,
 * are not questions this file should pretend to settle.
 *
 * Cost: at most nine 4-second chunks, mono, n_fft 2048 — about 3 000 FFTs and
 * two running medians over 270 bins; ~0.5 s on a desktop in total, one chunk
 * (~60 ms) per task, so the page stays interactive while it works.
 *
 * Public surface:
 *   DrumTrackDetect.analyze(audioBuffer) → Promise<{kind, p, features:{hpss, floorDb}, ms, sec}>
 *   DrumTrackDetect.features(mono, sampleRate)   → {hpss, floorDb}     (sync, pure)
 *   DrumTrackDetect.classify({hpss, floorDb})    → {kind, p}
 */
(function (root) {
'use strict';

var N_FFT = 2048, HOP = 512, K = 17;             // median kernels: 17 frames (~0.2 s) / 17 bins (~370 Hz)
var BAND_LO = 150, BAND_HI = 6000;
var CHUNK_SEC = 4, REGIONS = 3, PER_REGION = 3;   // ≤ 36 s analysed, in slices of ~4 s (one slice ≈ 60 ms of work)

// Logistic model over the two z-scored features of ONE 4-second chunk, class-balanced,
// fitted 2026-09-19 on this module's own output: 1 642 chunks of MUSDB18-7s train mixes and
// instrumentals against real drum stems, drum stems as two separators return them, and
// renders of three kit families. A FILE's p is the mean over its chunks (see aggregate()).
// Scored on held-out items the fit never saw:
//
//                               whole songs                      drum tracks
//   shipped restShare > 0.40    missed 21.5 %                    accused 16.3 %
//   this module, 7-s clips      'song' 85 · asked 12 · MISSED 3.6 'drums' 93 · asked 5 · 'song' 1.3
//   minute-long files           'song' 100                        'drums' 80–100, 'song' 0
//   song with a drum BREAK      'song' 86 · asked 14 · missed 0
//   song, first THIRD is drums  'song' 57 · asked 43 · missed 0
//   drum track with 6-s gaps                                      'drums' 86 · asked 14 · 'song' 0
//
// The 7-second misses are excerpts that ARE drums for their whole length. The two
// thresholds are not symmetric because the two mistakes are not: a song taken for drums
// costs the visitor a wrong result, a drum track taken for 'unsure' costs one extra click
// on «это барабанная дорожка».
var MODEL = { mu: [0.4568, -19.1578], sd: [0.2289, 12.3323], w: [2.996, 4.037], b: -3.612 };
var P_SONG = 0.60, P_DRUMS = 0.12;

var cosT = null, sinT = null, rev = null, win = null;
function init() {
    if (cosT) return;
    var n = N_FFT, i;
    cosT = new Float32Array(n / 2); sinT = new Float32Array(n / 2); rev = new Uint32Array(n); win = new Float32Array(n);
    for (i = 0; i < n / 2; i++) { cosT[i] = Math.cos(-2 * Math.PI * i / n); sinT[i] = Math.sin(-2 * Math.PI * i / n); }
    var bits = Math.log2(n);
    for (i = 0; i < n; i++) { var r = 0; for (var b = 0; b < bits; b++) if (i & (1 << b)) r |= 1 << (bits - 1 - b); rev[i] = r; }
    for (i = 0; i < n; i++) win[i] = 0.5 * (1 - Math.cos(2 * Math.PI * i / n));
}
function fft(re, im) {
    var n = re.length, i, j, t;
    for (i = 0; i < n; i++) { j = rev[i]; if (j > i) { t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; } }
    for (var len = 2; len <= n; len <<= 1) {
        var half = len >> 1, step = n / len;
        for (i = 0; i < n; i += len) for (j = 0; j < half; j++) {
            var k = j * step, wr = cosT[k], wi = sinT[k];
            var xr = re[i + j + half], xi = im[i + j + half];
            var tr = xr * wr - xi * wi, ti = xr * wi + xi * wr;
            re[i + j + half] = re[i + j] - tr; im[i + j + half] = im[i + j] - ti;
            re[i + j] += tr; im[i + j] += ti;
        }
    }
}

/**
 * Running median of width K over a strip. The window shrinks at the two ends (scipy's
 * 'reflect' would not — the band edges carry no decision weight, and the model was fitted
 * on THIS behaviour); an even-sized window answers with its upper median.
 *
 * A SLIDING sorted window, not a sort per position: one value leaves, one enters. The first
 * draft re-sorted 17 values at every one of ~190 000 positions per chunk and measured at
 * ~0.8 s per 4-second chunk in the page — six seconds of «the button is still disabled»
 * for one song. Same output to the bit (tests/js/drum-track-detect.test.mjs compares it
 * with a plain sort), a fifth of the time.
 */
function medianStrip(src, n, out, buf) {
    var h = K >> 1, m = 0, lo = 0, hi = -1, i, j, v, a, b, l, r, mid;
    for (i = 0; i < n; i++) {
        a = i - h < 0 ? 0 : i - h; b = i + h >= n ? n - 1 : i + h;
        while (hi < b) {                                  // a value enters on the right
            v = src[++hi]; l = 0; r = m;
            while (l < r) { mid = (l + r) >> 1; if (buf[mid] <= v) l = mid + 1; else r = mid; }
            for (j = m; j > l; j--) buf[j] = buf[j - 1];
            buf[l] = v; m++;
        }
        while (lo < a) {                                  // a value leaves on the left
            v = src[lo++]; l = 0; r = m;
            while (l < r) { mid = (l + r) >> 1; if (buf[mid] < v) l = mid + 1; else r = mid; }
            for (j = l; j < m - 1; j++) buf[j] = buf[j + 1];
            m--;
        }
        out[i] = buf[m >> 1];
    }
}

/** Harmonic/percussive energy of one window. Returns {h, e} (harmonic energy, total energy) and frame energies. */
function windowStats(mono, from, to, sr) {
    init();
    var lo = Math.max(1, Math.round(BAND_LO * N_FFT / sr)), hi = Math.min(N_FFT / 2, Math.round(BAND_HI * N_FFT / sr));
    var bins = hi - lo + 1, frames = Math.max(0, Math.floor((to - from - N_FFT) / HOP) + 1);
    if (frames < K + 2) return { h: 0, e: 0 };
    var A = new Float32Array(frames * bins), re = new Float32Array(N_FFT), im = new Float32Array(N_FFT), f, k, i;
    for (f = 0; f < frames; f++) {
        var off = from + f * HOP;
        for (i = 0; i < N_FFT; i++) { re[i] = mono[off + i] * win[i]; im[i] = 0; }
        fft(re, im);
        for (k = 0; k < bins; k++) { var r = re[lo + k], q = im[lo + k]; A[f * bins + k] = Math.sqrt(r * r + q * q); }
    }
    // H: median along TIME per bin;  P: median along FREQUENCY per frame
    var H = new Float32Array(frames * bins), P = new Float32Array(frames * bins);
    var strip = new Float32Array(Math.max(frames, bins)), outS = new Float32Array(Math.max(frames, bins)), buf = new Float32Array(K);
    for (k = 0; k < bins; k++) {
        for (f = 0; f < frames; f++) strip[f] = A[f * bins + k];
        medianStrip(strip, frames, outS, buf);
        for (f = 0; f < frames; f++) H[f * bins + k] = outS[f];
    }
    for (f = 0; f < frames; f++) {
        for (k = 0; k < bins; k++) strip[k] = A[f * bins + k];
        medianStrip(strip, bins, outS, buf);
        for (k = 0; k < bins; k++) P[f * bins + k] = outS[k];
    }
    var h = 0, e = 0;
    for (i = 0; i < A.length; i++) { var a2 = A[i] * A[i], h2 = H[i] * H[i], p2 = P[i] * P[i]; e += a2; h += a2 * h2 / (h2 + p2 + 1e-12); }
    return { h: h, e: e };
}

function frameEnergies(mono, from, to, into) {
    var n = 1024;
    for (var o = from; o + n <= to; o += n) { var s = 0; for (var i = 0; i < n; i++) { var v = mono[o + i]; s += v * v; } into.push(s / n + 1e-12); }
}
function percentile(sorted, q) {
    if (!sorted.length) return 0;
    var pos = (sorted.length - 1) * q, b = Math.floor(pos), r = pos - b;
    return b + 1 < sorted.length ? sorted[b] * (1 - r) + sorted[b + 1] * r : sorted[b];
}
function floorDbOf(energies) {
    if (energies.length < 8) return 0;
    var max = 0, i; for (i = 0; i < energies.length; i++) if (energies[i] > max) max = energies[i];
    var live = energies.filter(function (e) { return e > max * 1e-6; });      // digital silence is not «the floor»
    if (live.length <= 8) live = energies.slice();
    live.sort(function (a, b) { return a - b; });
    return 10 * Math.log10(percentile(live, 0.10) / percentile(live, 0.90));
}

/** The 4-second chunks that get analysed: the whole file when it is short, else three regions
 *  (around 20 / 50 / 80 % of the length) of three chunks each. One chunk = one slice of work. */
function windowsOf(length, sr) {
    var w = Math.round(CHUNK_SEC * sr), out = [], a;
    if (length <= w * REGIONS * PER_REGION * 1.1) {
        for (a = 0; a + w <= length; a += w) out.push([a, a + w]);
        if (!out.length) out.push([0, length]);
        else if (length - out[out.length - 1][1] > w / 2) out.push([length - w, length]);
        return out;
    }
    for (var i = 0; i < REGIONS; i++) {
        var c = Math.round(length * (0.2 + 0.3 * i)), start = Math.max(0, Math.min(length - w * PER_REGION, c - ((w * PER_REGION) >> 1)));
        for (var j = 0; j < PER_REGION; j++) out.push([start + j * w, start + (j + 1) * w]);
    }
    return out;
}

/** One chunk → its own two features and its level. */
function chunkFeatures(mono, from, to, sr) {
    var s = windowStats(mono, from, to, sr), en = [], e2 = 0;
    frameEnergies(mono, from, to, en);
    for (var i = 0; i < en.length; i++) e2 += en[i];
    return { hpss: s.e > 0 ? s.h / s.e : 0, floorDb: floorDbOf(en), level: en.length ? e2 / en.length : 0 };
}

function probOf(ft) {
    var z = MODEL.b + MODEL.w[0] * (ft.hpss - MODEL.mu[0]) / MODEL.sd[0] + MODEL.w[1] * (ft.floorDb - MODEL.mu[1]) / MODEL.sd[1];
    return 1 / (1 + Math.exp(-z));
}

/**
 * The file's verdict is the MEAN of its chunks' verdicts — not one verdict over pooled
 * statistics. Pooling was the first design and it failed its own unit test: a song with
 * a quiet drum intro has a huge 10th-to-90th percentile spread ACROSS sections, which is
 * exactly what «a kit that falls silent between hits» looks like to a pooled floor, and
 * the song came back 'drums' at p = 0.002. Inside one 4-second chunk the floor means what
 * it was meant to. Chunks 30 dB under the loudest one are left out: a fade or a gap has
 * nothing to say about what the file is.
 */
function aggregate(chunks) {
    var top = 0, i;
    for (i = 0; i < chunks.length; i++) if (chunks[i].level > top) top = chunks[i].level;
    var live = chunks.filter(function (c) { return c.level > top * 1e-3; });
    if (!live.length) return { p: 0, hpss: 0, floorDb: 0, n: 0 };
    var p = 0, h = 0, f = 0;
    for (i = 0; i < live.length; i++) { p += probOf(live[i]); h += live[i].hpss; f += live[i].floorDb; }
    return { p: p / live.length, hpss: h / live.length, floorDb: f / live.length, n: live.length };
}
function kindOf(p) { return p >= P_SONG ? 'song' : (p <= P_DRUMS ? 'drums' : 'unsure'); }

/** Sync twin of analyze() for tests and the lab: the same chunks, the same aggregation. */
function features(mono, sr) {
    var ws = windowsOf(mono.length, sr), chunks = [];
    for (var i = 0; i < ws.length; i++) chunks.push(chunkFeatures(mono, ws[i][0], ws[i][1], sr));
    var a = aggregate(chunks);
    return { hpss: a.hpss, floorDb: a.floorDb, p: a.p, chunks: chunks };
}

/** One chunk's features → {kind, p}. (A FILE's verdict comes from analyze()/features().p.) */
function classify(ft) {
    var p = probOf(ft);
    return { kind: kindOf(p), p: +p.toFixed(3) };
}

function monoOf(buffer) {
    var n = buffer.length, a = buffer.getChannelData(0);
    if (buffer.numberOfChannels < 2) return a;
    var b = buffer.getChannelData(1), m = new Float32Array(n);
    for (var i = 0; i < n; i++) m[i] = (a[i] + b[i]) * 0.5;
    return m;
}
function pause() { return new Promise(function (r) { setTimeout(r, 0); }); }

async function analyze(buffer) {
    var t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    var sr = buffer.sampleRate, mono = monoOf(buffer);
    await pause();
    var ws = windowsOf(mono.length, sr), chunks = [];
    for (var i = 0; i < ws.length; i++) {
        chunks.push(chunkFeatures(mono, ws[i][0], ws[i][1], sr));
        await pause();                                   // one chunk = one task: the page stays alive
    }
    var a = aggregate(chunks);
    return {
        kind: kindOf(a.p), p: +a.p.toFixed(3),
        features: { hpss: +a.hpss.toFixed(4), floorDb: +a.floorDb.toFixed(2) }, chunks: a.n,
        sec: Math.round(buffer.duration), ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0)
    };
}

var api = { analyze: analyze, features: features, classify: classify, chunkFeatures: chunkFeatures, aggregate: aggregate, medianStrip: medianStrip, K: K, MODEL: MODEL, P_SONG: P_SONG, P_DRUMS: P_DRUMS, version: '1.0.0' };
root.DrumTrackDetect = api;
if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this));
